import { useEffect, useState } from 'react'
import { resolverCinematica, type EntradaSolucionador, type ResultadoFisica } from './solucionador'

/**
 * Estado do carregamento da engine e da resolução.
 *
 * `indisponivel` existe porque o motor é WebAssembly carregado sob demanda: se o
 * download falhar ou o navegador recusar o módulo, a cena tem de continuar
 * funcionando pelo caminho procedural em vez de abrir em branco.
 */
export type EstadoFisica = 'desligada' | 'carregando' | 'pronta' | 'indisponivel'

/**
 * Carrega o motor uma única vez por sessão, em import dinâmico.
 *
 * O pacote embute ~2 MB de WebAssembly em base64. Em import estático isso entra
 * no pacote inicial e todo aluno paga por ele só para abrir a maquete; assim só
 * paga quem liga a física na aba de cinemática, e o Vite separa o pedaço sozinho.
 */
let motor: Promise<typeof import('@dimforge/rapier3d-compat')> | null = null

function carregarMotor() {
  if (!motor) {
    motor = import('@dimforge/rapier3d-compat').then(async (modulo) => {
      await modulo.init()
      return modulo
    })
    // um carregamento que falha não pode envenenar as tentativas seguintes
    motor.catch(() => {
      motor = null
    })
  }
  return motor
}

export interface FisicaCarga {
  resultado: ResultadoFisica | null
  estado: EstadoFisica
}

/**
 * Resolve as trajetórias dos ocupantes e objetos quando a configuração muda.
 *
 * A resolução é feita UMA vez por configuração, não a cada quadro: o resultado é
 * uma tabela de quadros que a cena amostra pela mesma régua 0..1 da timeline. É
 * o que permite arrastar a régua para trás — física não roda ao contrário — e é
 * o que garante que o mesmo acidente repita a mesma pose.
 *
 * `entrada` deve vir memoizada: é a identidade dela que dispara a resolução.
 */
export function useFisicaCarga(entrada: EntradaSolucionador | null): FisicaCarga {
  const [fisica, setFisica] = useState<FisicaCarga>({ resultado: null, estado: 'desligada' })

  useEffect(() => {
    if (!entrada) {
      setFisica({ resultado: null, estado: 'desligada' })
      return
    }

    let cancelado = false
    setFisica((atual) => ({ resultado: atual.resultado, estado: 'carregando' }))

    carregarMotor()
      .then((R) => {
        if (cancelado) return
        setFisica({ resultado: resolverCinematica(R, entrada), estado: 'pronta' })
      })
      .catch(() => {
        if (cancelado) return
        setFisica({ resultado: null, estado: 'indisponivel' })
      })

    return () => {
      cancelado = true
    }
  }, [entrada])

  return fisica
}
